import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import Footer from '../components/Footer';
import SectionHeading from '../components/SectionHeading';

interface CaseStudy {
  title: string;
  location: string;
  description: string;
  highlights: string[];
}

interface BreakImage {
  src: string;
  alt: string;
  objectPosition?: string;
}

interface Testimonial {
  quote: string;
  name: string;
  title: string;
}

type QuoteFlow = 'lead' | 'inset' | 'wide';

const CaseStudyCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
  backgroundColor: '#FFFFFF',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${theme.palette.primary.light}`,
  },
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
}));

const caseStudies: CaseStudy[] = [
  {
    title: 'Incline Creek Estates',
    location: 'Incline Village, NV',
    description:
      'Planning, entitlement, and delivery of a 58-home single-family community on 13 acres in the heart of Incline Village. The work paired mountain residential design with recreation access, LEED-focused materials, and smart home features.',
    highlights: [
      '58 homes, 4 bed ~2,700 sq ft, 13 acres',
      'LEED-certified insulation and decking; Smart Home Technology',
      '$55 million investment; starting at $1.26 million',
    ],
  },
  {
    title: 'Country Club Villas',
    location: 'Incline Village, NV',
    description:
      'Fifty multi-family luxury townhomes developed by Conte Development with Scott Properties as consultant and broker of record. Three bedroom, 2.5 bath floor plans, approximately 2,100 sq ft. Adjacent to the Hyatt Regency Lake Tahoe, within walking distance to Hyatt Casino and Incline Village beaches, and a five-minute drive to Diamond Peak Ski Resort.',
    highlights: [
      '50 townhomes, 3 bed 2.5 bath ~2,100 sq ft',
      'Adjacent to Hyatt Regency Lake Tahoe',
      '$20 million investment',
    ],
  },
  {
    title: 'Village Plaza',
    location: 'Incline Village, NV',
    description:
      'A 22,000 sq ft retail and office campus at the main intersection of Incline Village. Scott Development co-developed the project with RBT–Nevada, overseeing gas station conversion, assembly of multiple parcels, land use planning, and traffic and scenic analysis. Today the plaza is a landmark for Incline Village.',
    highlights: [
      'Tenants: Starbucks, Quizno\'s, Keva Juice, Coldwell Banker, Tahoe Lending, First Centennial Title',
      'Gas station to mixed-use conversion; multi-parcel assembly',
      '$10 million investment',
    ],
  },
  {
    title: 'Championship Golf Course Villas',
    location: 'Incline Village, NV',
    description:
      'Development of 14 luxury town homes with custom finishes and direct access to the Championship Golf course. Average 2,200 sq ft with 3 bed, 2.5 bath. Custom finish work and mountain views.',
    highlights: [
      '14 luxury town homes, ~2,200 sq ft, 3 bed 2.5 bath',
      'Custom finish work and mountain views',
      '$15 million investment',
    ],
  },
  {
    title: 'Indian Hills Retail Plaza',
    location: 'Carson Valley, NV',
    description:
      'A 30,000 sq ft retail center in South Carson Valley developed by Staedler & Scott Holdings, Inc. and affiliates, including Scott Development. The center hosts both in-line and big-box tenants serving the community.',
    highlights: [
      '30,000 sq ft; in-line and big-box tenants',
      'Sports bar, insurance, veterinarian, real estate, pizza, retail',
      '$6 million investment',
    ],
  },
  {
    title: 'Incline Athletic Club',
    location: 'Incline Village, NV',
    description:
      'Conversion of a once-dilapidated racquetball club into a 22,000 sq ft high-end commercial office and full-scale athletic facility. The building is home to the Incline Athletic Club as well as professional, medical, and health offices that serve Incline Village.',
    highlights: [
      '22,000 sq ft; building conversion',
      'Full-scale athletic facility and professional/medical offices',
      '$8.5 million investment',
    ],
  },
  {
    title: 'Ridgeview Estates',
    location: 'Incline Village, NV',
    description:
      'High-end custom home community in Incline Village. Each lake-view home is four bedroom, 3.5 bath, approximately 3,500 sq ft. Custom built by Scott Development in conjunction with Brent Robinson Construction, with high-quality design and craftsmanship.',
    highlights: [
      'Custom lake-view homes; 4 bed, 3.5 bath, ~3,500 sq ft',
      'Scott Development with Brent Robinson Construction',
      '$8 million investment',
    ],
  },
];

const caseStudyGroups = caseStudies.reduce<CaseStudy[][]>(
  (groups, study, index) => {
    if (index % 2 === 0) {
      groups.push([study]);
    } else {
      groups[groups.length - 1].push(study);
    }

    return groups;
  },
  []
);

const breakImages: BreakImage[] = [
  { src: '/images/4748_better.png', alt: 'Country Club Villas' },
  {
    src: '/images/starbucks_better.png',
    alt: 'Village Plaza',
    objectPosition: 'center 30%',
  },
  { src: '/images/gcv_better.png', alt: 'Championship Golf Course Villas' },
  { src: '/images/ice2_better.png', alt: 'Incline Village' },
];

const galleryImages = [
  { src: '/images/ice_better.png', alt: 'Incline Creek Estates' },
  { src: '/images/nice1_better.png', alt: 'Scott Development projects' },
  { src: '/images/nice2_better.png', alt: 'Scott Development projects' },
];

const testimonials: Testimonial[] = [
  {
    quote:
      'Scott Companies has the experience—and more importantly, the relationships—that are essential to getting a project through the red tape in Tahoe.',
    name: 'Roger Wittenberg',
    title: 'CEO, International Supply Consortium (ISC), founder of Trex® Decking',
  },
  {
    quote:
      'The Scott team understands the importance of communicating with investors. They stay on top of projects and keep everyone informed of what\'s going on.',
    name: 'Rudy Staedler',
    title: 'BSM Communities, Investor/Partner',
  },
  {
    quote:
      'Vince takes the lead on complicated projects with calm, positive composure and goals that are clearly defined. He navigates funders, government agencies, and private parties toward developments with lasting environmental and social benefit.',
    name: 'Jen Mahler',
    title: 'Construction and development industry, 30+ years',
  },
  {
    quote:
      'As owner of Scott Development, Vince has overseen acquisition, entitlement, land planning, financing, and execution of complex residential, commercial, and mixed-use projects across Northern California.',
    name: 'Scott Carlton',
    title: 'Director of Public & Government Relations, Wilks Brothers',
  },
];

const testimonialPlacements = {
  afterFirstBreak: testimonials[0],
  beforeSecondBreak: testimonials[1],
  afterThirdBreak: testimonials[2],
  besideRidgeview: testimonials[3],
};

const testimonialFlowStyles: Record<QuoteFlow, object> = {
  lead: {
    maxWidth: { xs: '100%', md: 'min(92%, 52rem)' },
    ml: { md: 0 },
    mr: { md: 'auto' },
  },
  inset: {
    maxWidth: { xs: '100%', md: 'min(88%, 44rem)' },
    ml: { md: '8%' },
    mr: { md: 'auto' },
  },
  wide: {
    maxWidth: { xs: '100%', md: 'min(94%, 56rem)' },
    mx: { md: 'auto' },
  },
};

const testimonialFlowMargins: Record<QuoteFlow, object> = {
  lead: { xs: 5, md: 6 },
  inset: { xs: 6, md: 8 },
  wide: { xs: 5, md: 7 },
};

function isRidgeviewOnlyGroup(group: CaseStudy[]) {
  return group.length === 1 && group[0].title === 'Ridgeview Estates';
}

function TestimonialCardBody({ testimonial }: { testimonial: Testimonial }) {
  return (
    <>
      <Typography
        component="blockquote"
        variant="body1"
        sx={{
          fontStyle: 'italic',
          color: 'text.primary',
          m: 0,
          mb: 2,
          lineHeight: 1.65,
        }}
      >
        &ldquo;{testimonial.quote}&rdquo;
      </Typography>
      <Typography variant="subtitle2" color="primary" fontWeight={600}>
        {testimonial.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {testimonial.title}
      </Typography>
    </>
  );
}

function FullWidthTestimonial({
  testimonial,
  flow,
}: {
  testimonial: Testimonial;
  flow: QuoteFlow;
}) {
  return (
    <Box
      sx={{
        width: 1,
        ...testimonialFlowStyles[flow],
        my: testimonialFlowMargins[flow],
      }}
    >
      <TestimonialCard elevation={0} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ py: { xs: 3, md: 4 }, px: { xs: 2.5, md: 4 } }}>
          <TestimonialCardBody testimonial={testimonial} />
        </CardContent>
      </TestimonialCard>
    </Box>
  );
}

function CaseStudyCardContent({
  study,
}: {
  study: CaseStudy;
}) {
  return (
    <CaseStudyCard>
      <CardContent>
        <Typography variant="h4" color="primary" gutterBottom>
          {study.title}
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          gutterBottom
        >
          {study.location}
        </Typography>
        <Typography variant="body1" paragraph>
          {study.description}
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          {study.highlights.map((highlight, idx) => (
            <Typography
              component="li"
              key={idx}
              variant="body2"
              color="text.secondary"
            >
              {highlight}
            </Typography>
          ))}
        </Box>
      </CardContent>
    </CaseStudyCard>
  );
}

export default function CaseStudies() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minWidth: '100vw',
        minHeight: '100vh',
        bgcolor: 'background.default',
        mt: { xs: 0, md: '3.5rem' },
      }}
    >
      <Container sx={{ py: 8 }}>
        <SectionHeading
          eyebrow="Case Studies"
          title="Work delivered with discipline."
          description="Completed project profiles showing how strategy, entitlement, design, and execution came together across Scott Development's portfolio."
          align="center"
        />

        {/* Hero image */}
        <Box
          component="img"
          src="/images/ice3_remastered.png"
          alt="Incline Creek Estates"
          sx={{
            width: '100%',
            height: { xs: 300, sm: 380, md: 460 },
            objectFit: 'cover',
            borderRadius: 2,
            mt: 4,
            mb: 6,
          }}
        />

        {/* Completed studies with full-width quotes woven in + Ridgeview paired with a quote. */}
        {caseStudyGroups.map((group, groupIndex) => (
          <Box key={groupIndex}>
            {isRidgeviewOnlyGroup(group) ? (
              <Grid container spacing={4} alignItems="stretch">
                <Grid item xs={12} md={6}>
                  <CaseStudyCardContent study={group[0]} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TestimonialCard
                    elevation={0}
                    sx={{ height: '100%', borderRadius: 3 }}
                  >
                    <CardContent
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        py: { xs: 3, md: 4 },
                        px: { xs: 2.5, md: 3.5 },
                      }}
                    >
                      <TestimonialCardBody
                        testimonial={testimonialPlacements.besideRidgeview}
                      />
                    </CardContent>
                  </TestimonialCard>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={4}>
                {group.map((study) => (
                  <Grid item key={study.title} xs={12} md={6}>
                    <CaseStudyCardContent study={study} />
                  </Grid>
                ))}
              </Grid>
            )}
            {groupIndex < caseStudyGroups.length - 1 &&
              groupIndex < breakImages.length && (
                <>
                  {groupIndex === 1 && (
                    <FullWidthTestimonial
                      testimonial={testimonialPlacements.beforeSecondBreak}
                      flow="inset"
                    />
                  )}
                  <Box
                    component="img"
                    src={breakImages[groupIndex].src}
                    alt={breakImages[groupIndex].alt}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: '100%',
                      height: { xs: 280, sm: 340, md: 420 },
                      objectFit: 'cover',
                      objectPosition: breakImages[groupIndex].objectPosition,
                      borderRadius: 2,
                      display: 'block',
                      mt:
                        groupIndex === 1
                          ? { xs: 3, md: 4 }
                          : { xs: 5, md: 6 },
                      mb: groupIndex === 0 || groupIndex === 2 ? 0 : 6,
                    }}
                  />
                  {groupIndex === 0 && (
                    <FullWidthTestimonial
                      testimonial={testimonialPlacements.afterFirstBreak}
                      flow="lead"
                    />
                  )}
                  {groupIndex === 2 && (
                    <FullWidthTestimonial
                      testimonial={testimonialPlacements.afterThirdBreak}
                      flow="wide"
                    />
                  )}
                </>
              )}
          </Box>
        ))}

        {/* Gallery row */}
        <Grid container spacing={2} sx={{ mt: 4, mb: 2 }}>
          {galleryImages.map((img, i) => (
            <Grid item key={i} xs={12} sm={4}>
              <Box
                component="img"
                src={img.src}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: { xs: 280, sm: 340, md: 400 },
                  objectFit: 'cover',
                  borderRadius: 2,
                }}
              />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body2" color="text.secondary">
            Formal reference letters and banking references available upon
            request.{' '}
            <Link
              component={RouterLink}
              to="/contact"
              color="primary"
              fontWeight={600}
            >
              Contact us
            </Link>
          </Typography>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
